import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import Box from '@mui/material/Box';
import FormControl from '@mui/material/FormControl';
import IconButton from '@mui/material/IconButton';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import PersonIcon from '@mui/icons-material/Person';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import CircularProgress from '@mui/material/CircularProgress';
import axiosInstance from '../../../fetchConfig/axiosConfig.js';
import { useUser } from '../../UserContext.js';
import { usePersistedDataStore } from '../../store.js';
import logger from '../../../utils/logger';
import {
  CARD_BORDER_RADIUS,
  RIGHT_RAIL_GAP,
  RIGHT_RAIL_WIDTH,
} from './constants.js';
import {
  achievedYearBucket,
  avIfmvMetricLines,
  defaultResultTypeFromCards,
  formatPortfolioReduction,
  formatWinRateLine,
  formatWinsLine,
  municipalResultsPersistEntries,
  resultTypeDisplayLabel,
} from './utils.js';
import MunicipalityDetailPanel from './MunicipalityDetailPanel.js';

const LR = '[MunicipalResultsBanner] load_municipal_results';
const LR_TOWN_CONTACTS = '[MunicipalResultsBanner] load_town_contacts';
const NOTES_SAVE_DEBOUNCE_MS = 1000;
const MUNICIPAL_BANNER_DEBUG = false;
const OFFER_CARD_SCOPE = { aventine: 'aventine_offer', town: 'town_offer' };
const RESULT_TYPE_OPTIONS = [
  { value: 'All', label: 'All' },
  { value: 'SCAR', label: 'SCAR' },
  { value: 'BAR', label: 'BAR' },
];

const CURRENT_OFFERS_TOOLTIP = 'This only considers the entered offers, blank offers do not get counted';
const RUNNING_PORTFOLIO_TOOLTIP = 'This assumes No reduction on cases which do not have an offer.';

function OfferInfoIcon({ title }) {
  return (
    <Tooltip title={title} placement="top">
      <Box
        component="span"
        sx={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: '0.72rem', lineHeight: 0 }}
      >
        <InfoOutlinedIcon sx={{ fontSize: '0.72rem', color: 'text.secondary', cursor: 'pointer', display: 'block' }} />
      </Box>
    </Tooltip>
  );
}

const offerStatLabelSx = {
  fontSize: '0.58rem',
  lineHeight: 1,
  whiteSpace: 'nowrap',
  textAlign: 'right',
};

const offerStatValueSx = {
  fontSize: '0.72rem',
  lineHeight: 1,
  fontWeight: 500,
  whiteSpace: 'nowrap',
  textAlign: 'left',
};

function OfferStatsGrid({ row }) {
  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: 'auto auto 0.72rem',
        columnGap: 0.75,
        rowGap: 0.12,
        alignItems: 'center',
      }}
    >
      <Typography variant="caption" color="text.secondary" sx={offerStatLabelSx}>
        Current Offers:
      </Typography>
      <Typography variant="body2" sx={offerStatValueSx}>
        {formatPortfolioReduction(row?.ratio)}
      </Typography>
      <OfferInfoIcon title={CURRENT_OFFERS_TOOLTIP} />

      <Typography variant="caption" color="text.secondary" sx={offerStatLabelSx}>
        Running Portfolio:
      </Typography>
      <Typography variant="body2" sx={offerStatValueSx}>
        {formatPortfolioReduction(row?.runningRatio)}
      </Typography>
      <OfferInfoIcon title={RUNNING_PORTFOLIO_TOOLTIP} />
    </Box>
  );
}

function PortfolioReductionCard({ title, borderColor, loading, row, metricLines }) {
  return (
    <Box
      sx={{
        flex: 1,
        minWidth: 148,
        p: 0.5,
        bgcolor: 'background.paper',
        borderRadius: CARD_BORDER_RADIUS,
        border: '2px solid',
        borderColor,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        gap: 0.12,
        textAlign: 'center',
      }}
    >
      <Typography
        variant="caption"
        color="text.secondary"
        fontWeight={600}
        sx={{ fontSize: '0.66rem', lineHeight: 1.2, px: 0.25 }}
      >
        {title}
      </Typography>
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 0.35 }}>
          <CircularProgress size={16} color="primary" />
        </Box>
      ) : (
        <>
          <OfferStatsGrid row={row} />
          {metricLines.map((line) => (
            <Typography
              key={line}
              variant="caption"
              color="text.secondary"
              sx={{ fontSize: '0.58rem', lineHeight: 1.15, mt: 0.05 }}
            >
              {line}
            </Typography>
          ))}
        </>
      )}
    </Box>
  );
}

const MunicipalResultsBanner = React.memo(() => {
  const { user } = useUser();
  const negotiationObj = usePersistedDataStore((state) => state.negotiationObj);
  const municode = negotiationObj?.MuniCode;
  const taxYear = negotiationObj?.TaxYear;
  const villageRun = negotiationObj?.villageRun ?? 0;
  const uids = negotiationObj?.uids;
  const uidsKey = Array.isArray(uids) && uids.length > 0 ? uids.join(',') : '';
  const municipalityString = negotiationObj?.muniString ?? '';
  const [resultType, setResultType] = useState('All');
  const [filterByUser, setFilterByUser] = useState(false);
  const [reductionCardIdx, setReductionCardIdx] = useState(0);
  const [data, setData] = useState(null);
  const [townContacts, setTownContacts] = useState(null);
  const [loadingResults, setLoadingResults] = useState(() => Boolean(municode && municode !== 'All' && taxYear));
  const [loadingAventineOffer, setLoadingAventineOffer] = useState(false);
  const [loadingTownOffer, setLoadingTownOffer] = useState(false);
  const [loadingContacts, setLoadingContacts] = useState(() => Boolean(municode && municode !== 'All'));
  const [municipalityNotes, setMunicipalityNotes] = useState('');
  const [isSavingNotes, setIsSavingNotes] = useState(false);
  const notesDebounceRef = useRef(null);
  const lastSavedNotesRef = useRef('');
  const canSaveNotes = Boolean(municode && municode !== 'All');
  const shouldFetch = Boolean(municode && municode !== 'All' && taxYear);

  useEffect(() => () => {
    if (notesDebounceRef.current) clearTimeout(notesDebounceRef.current);
  }, []);

  const saveMunicipalNotes = useCallback(async (notes) => {
    if (!canSaveNotes || notes === lastSavedNotesRef.current) return;
    setIsSavingNotes(true);
    try {
      await axiosInstance.post('/write_municipal_notes', {
        municode,
        notes,
      });
      lastSavedNotesRef.current = notes;
    } catch (err) {
      logger.error('[MunicipalResultsBanner] write_municipal_notes error', err?.response?.data ?? err?.message ?? err);
    } finally {
      setIsSavingNotes(false);
    }
  }, [municode, canSaveNotes]);

  const saveTownContacts = useCallback(async ({ negotiatorEmails, stipEmails }) => {
    if (!canSaveNotes) return;
    await axiosInstance.post('/write_town_contact', {
      municode,
      municipalityString,
      contactType: 'negotiator',
      emails: negotiatorEmails,
    });
    if (stipEmails !== undefined) {
      await axiosInstance.post('/write_town_contact', {
        municode,
        contactType: 'stip',
        emails: stipEmails,
      });
    }
    setTownContacts((prev) => {
      const offersEmails = negotiatorEmails ?? [];
      const stipsEmails = stipEmails !== undefined
        ? (stipEmails.length ? stipEmails : offersEmails)
        : (prev?.stips?.emails ?? []);
      return {
        offers: {
          ...(prev?.offers ?? {}),
          emails: offersEmails,
        },
        stips: {
          ...(prev?.stips ?? {}),
          emails: stipsEmails,
        },
      };
    });
  }, [municode, municipalityString, canSaveNotes]);

  const fetchTownContacts = useCallback(() => {
    if (!canSaveNotes) {
      setTownContacts(null);
      setLoadingContacts(false);
      return Promise.resolve();
    }
    setLoadingContacts(true);
    logger.log(`${LR_TOWN_CONTACTS} request`, { url: '/load_town_contacts', municode });
    return axiosInstance.post('/load_town_contacts', { municode })
      .then((res) => {
        const contacts = res.data?.success ? res.data.data : null;
        logger.log(`${LR_TOWN_CONTACTS} response`, { httpStatus: res.status, success: res.data?.success, hasContacts: !!contacts });
        setTownContacts(contacts);
      })
      .catch((err) => {
        logger.error(`${LR_TOWN_CONTACTS} error`, err?.response?.status, err?.response?.data ?? err?.message ?? err);
        setTownContacts(null);
      })
      .finally(() => setLoadingContacts(false));
  }, [municode, canSaveNotes]);

  const handleNotesChange = useCallback((notes) => {
    setMunicipalityNotes(notes);
    if (notesDebounceRef.current) clearTimeout(notesDebounceRef.current);
    notesDebounceRef.current = setTimeout(() => {
      saveMunicipalNotes(notes);
    }, NOTES_SAVE_DEBOUNCE_MS);
  }, [saveMunicipalNotes]);

  const fetchMunicipalResults = useCallback((options = {}) => {
    const scope = options.scope ?? 'all';
    if (!shouldFetch) return Promise.resolve();
    if (notesDebounceRef.current) clearTimeout(notesDebounceRef.current);

    if (scope === 'all') setLoadingResults(true);
    else if (scope === 'aventine') setLoadingAventineOffer(true);
    else if (scope === 'town') setLoadingTownOffer(true);

    const uidsList = uidsKey ? uidsKey.split(',') : [];
    const params = {
      municode,
      taxyear: parseInt(taxYear, 10),
      villageRun: villageRun === 1 || villageRun === true || villageRun === '1' ? 1 : 0,
      ...(filterByUser && user?.userId != null ? { negotiatorUserId: user.userId } : {}),
      ...(uidsList.length > 0 ? { uids: uidsList } : {}),
    };
    logger.log(`${LR} request`, { url: '/load_municipal_results', params, scope });
    return axiosInstance.post('/load_municipal_results', params)
      .then((res) => {
        const payload = res.data?.success ? res.data.data : null;
        logger.log(`${LR} response`, { httpStatus: res.status, success: res.data?.success, hasCards: !!payload?.cards, scope });
        if (!payload?.cards) {
          if (scope === 'all') setData(null);
          return;
        }

        if (scope === 'all') {
          const defaultResultType = defaultResultTypeFromCards(payload.cards, taxYear);
          setData(payload);
          setResultType(defaultResultType);
          const notes = payload.municipalityMeta?.notes ?? '';
          setMunicipalityNotes(notes);
          lastSavedNotesRef.current = notes;
          setReductionCardIdx(0);
          if (!filterByUser) {
            const stage = payload.cards[defaultResultType];
            const y = parseInt(taxYear, 10);
            usePersistedDataStore.getState().setSettledPerformance({
              resultType: defaultResultType,
              ...achievedYearBucket(stage?.achieved_by_year, taxYear),
              priorYears: [y - 1, y - 2].map((year) => ({
                year,
                ratio: achievedYearBucket(stage?.achieved_by_year, year)?.ratio ?? null,
              })),
              priorAvg: (stage?.achieved_views ?? []).find((view) => view.year == null)?.ratio ?? null,
            });
            const entries = municipalResultsPersistEntries(payload.cards, taxYear);
            if (entries.length) {
              axiosInstance.post('/write_municipal_results', { municode, entries })
                .catch((err) => {
                  logger.error('[MunicipalResultsBanner] write_municipal_results error', err?.response?.data ?? err?.message ?? err);
                });
            }
          }
          return;
        }

        const cardField = OFFER_CARD_SCOPE[scope];
        if (!cardField) return;

        setData((prev) => {
          if (!prev?.cards) return payload;
          const cards = { ...prev.cards };
          for (const key of Object.keys(payload.cards)) {
            if (!cards[key] || !payload.cards[key]) continue;
            cards[key] = { ...cards[key], [cardField]: payload.cards[key][cardField] };
          }
          return { ...prev, cards };
        });
      })
      .catch((err) => {
        logger.error(`${LR} error`, err?.response?.status, err?.response?.data ?? err?.message ?? err);
        if (scope === 'all') setData(null);
      })
      .finally(() => {
        if (scope === 'all') setLoadingResults(false);
        else if (scope === 'aventine') setLoadingAventineOffer(false);
        else if (scope === 'town') setLoadingTownOffer(false);
      });
  }, [municode, taxYear, villageRun, shouldFetch, filterByUser, user?.userId, uidsKey]);

  useEffect(() => {
    if (!shouldFetch) return;
    const setRefresh = usePersistedDataStore.getState().setRefreshMunicipalResults;
    setRefresh((options) => {
      const scope = options?.scope ?? 'all';
      fetchMunicipalResults(options);
      if (scope === 'all') fetchTownContacts();
    });
    return () => { setRefresh(null); };
  }, [fetchMunicipalResults, fetchTownContacts, shouldFetch]);

  useEffect(() => {
    if (!shouldFetch) {
      setTownContacts(null);
      return;
    }
    fetchTownContacts();
  }, [fetchTownContacts, shouldFetch]);

  useEffect(() => {
    if (!shouldFetch) return;
    fetchMunicipalResults({ scope: 'all' });
  }, [fetchMunicipalResults, shouldFetch]);

  const stageCards = useMemo(() => data?.cards?.[resultType] ?? null, [data, resultType]);

  const reductionCards = useMemo(() => {
    const views = stageCards?.achieved_views ?? [];
    const y = parseInt(taxYear, 10);
    return views.filter((v) => v.year == null || v.year !== y);
  }, [stageCards, taxYear]);

  const casetypeTotalCases = useMemo(
    () => stageCards?.offerScopeTotalCases
      ?? achievedYearBucket(stageCards?.achieved_by_year, taxYear)?.totalCases,
    [stageCards, taxYear]
  );

  const defaultReductionCardIdx = 0;
  const reductionCard = reductionCards[reductionCardIdx] ?? reductionCards[defaultReductionCardIdx];

  const masterPetition = data?.municipalityMeta?.masterPetition === true;
  const barReview = data?.municipalityMeta?.barReview;
  const barReviewActive = barReview === true;
  const barReviewTooltip = barReview === true
    ? 'Historically, this municipality conducts reasonably fair BAR review'
    : barReview === false
      ? 'Historically, this municipality does not conduct BAR review'
      : 'Historically, BAR review status unknown';

  const handleResultTypeChange = (value) => {
    setResultType(value);
    setReductionCardIdx(0);
  };

  const cycleReductionCard = (dir) => {
    const n = reductionCards.length || 1;
    setReductionCardIdx((i) => (i + dir + n) % n);
  };

  if (!shouldFetch) return null;

  const reductionCardTitle = loadingResults
    ? `${resultTypeDisplayLabel(resultType)} Achieved Reduction`
    : reductionCard?.year == null
      ? `Past 2 Years ${resultTypeDisplayLabel(resultType)} Average`
      : `${resultTypeDisplayLabel(resultType)} Achieved Reduction (${reductionCard.label})`;

  return (
    <Box sx={{
      display: 'flex',
      flexDirection: 'column',
      gap: 0.75,
      flexShrink: 0,
      px: 1,
      py: 0.65,
      bgcolor: 'grey.100',
      borderRadius: CARD_BORDER_RADIUS,
      mb: 1,
      minHeight: 168,
    }}
    >
      <Box sx={{ display: 'flex', alignItems: 'stretch', gap: 1 }}>
        <Box sx={{ display: 'flex', gap: 0.75, flex: 1, minWidth: 0, flexWrap: 'nowrap', overflowX: 'auto', alignItems: 'stretch' }}>
          <Box
            sx={{
              flex: 1,
              p: 0.5,
              bgcolor: 'background.paper',
              borderRadius: CARD_BORDER_RADIUS,
              textAlign: 'center',
              cursor: 'pointer',
              userSelect: 'none',
              position: 'relative',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
            }}
            onClick={() => cycleReductionCard(-1)}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') cycleReductionCard(-1); }}
          >
            <Tooltip title="Previous">
              <IconButton
                onClick={(e) => { e.stopPropagation(); cycleReductionCard(-1); }}
                aria-label="Previous portfolio reduction"
                sx={{ position: 'absolute', top: '50%', left: 2, transform: 'translateY(-50%)', p: 0.25 }}
                size="small"
              >
                <ChevronLeftIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Next">
              <IconButton
                onClick={(e) => { e.stopPropagation(); cycleReductionCard(1); }}
                aria-label="Next portfolio reduction"
                sx={{ position: 'absolute', top: '50%', right: 2, transform: 'translateY(-50%)', p: 0.25 }}
                size="small"
              >
                <ChevronRightIcon fontSize="small" />
              </IconButton>
            </Tooltip>

            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flexWrap: 'wrap', gap: 0.25 }}>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.66rem', lineHeight: 1.2 }}>
                {reductionCardTitle}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 0.1, minHeight: 22 }}>
              {loadingResults ? (
                <CircularProgress size={16} color="primary" />
              ) : (
                <>
              <Typography variant="body2" fontWeight={500} sx={{ fontSize: '0.76rem', lineHeight: 1.2 }}>
                {formatPortfolioReduction(reductionCard?.ratio)}
              </Typography>
              {MUNICIPAL_BANNER_DEBUG && avIfmvMetricLines(reductionCard).map((line) => (
                <Typography key={line} variant="caption" color="text.secondary" sx={{ fontSize: '0.6rem' }}>
                  {line}
                </Typography>
              ))}
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.6rem', lineHeight: 1.15 }}>
                {formatWinRateLine(reductionCard?.wins, reductionCard?.totalCases)}
              </Typography>
                </>
              )}
            </Box>
          </Box>

          <PortfolioReductionCard
            title="Aventine Offer Requested Reduction"
            borderColor="success.main"
            loading={loadingResults || loadingAventineOffer}
            row={stageCards?.aventine_offer}
            metricLines={[
              ...(MUNICIPAL_BANNER_DEBUG ? avIfmvMetricLines(stageCards?.aventine_offer) : []),
              formatWinsLine(stageCards?.aventine_offer?.wins, casetypeTotalCases),
            ]}
          />

          <PortfolioReductionCard
            title="Town Offer Requested Reduction"
            borderColor="warning.main"
            loading={loadingResults || loadingTownOffer}
            row={stageCards?.town_offer}
            metricLines={[
              ...(MUNICIPAL_BANNER_DEBUG ? avIfmvMetricLines(stageCards?.town_offer) : []),
              formatWinsLine(stageCards?.town_offer?.wins, casetypeTotalCases),
            ]}
          />
        </Box>

        <Box
          sx={{
            width: RIGHT_RAIL_WIDTH,
            flexShrink: 0,
            display: 'flex',
            alignItems: 'center',
            gap: RIGHT_RAIL_GAP,
          }}
        >
          <FormControl size="small" sx={{ flex: 1, minWidth: 0 }}>
            <InputLabel id="result-type-label">Results</InputLabel>
            <Select
              fullWidth
              labelId="result-type-label"
              value={resultType}
              label="Results"
              disabled={loadingResults}
              onChange={(e) => handleResultTypeChange(e.target.value)}
            >
              {RESULT_TYPE_OPTIONS.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <Tooltip title={filterByUser ? 'Show all negotiators' : 'Show only my cases'}>
            <IconButton
              size="small"
              color={filterByUser ? 'primary' : 'default'}
              disabled={loadingResults}
              onClick={() => setFilterByUser((v) => !v)}
              aria-label={filterByUser ? 'Show all' : 'Filter by me'}
              sx={{ flexShrink: 0, ml: 0.25 }}
            >
              <PersonIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      <MunicipalityDetailPanel
        municipalityNotes={municipalityNotes}
        onNotesChange={handleNotesChange}
        isSavingNotes={isSavingNotes}
        contacts={townContacts}
        onSaveTownContacts={saveTownContacts}
        loadingNotes={loadingResults}
        loadingContacts={loadingContacts}
        loadingIndicators={loadingResults}
        masterPetition={masterPetition}
        barReviewActive={barReviewActive}
        barReviewTooltip={barReviewTooltip}
      />
    </Box>
  );
});

MunicipalResultsBanner.displayName = 'MunicipalResultsBanner';

export default MunicipalResultsBanner;
